import type { ItemStatus } from "@iltocat/shared";
import { ApiError, api } from "./api.js";

/**
 * One change applied to many items, through the same routes the item screen
 * uses, so a bulk change is refused exactly where the single one would be.
 *
 * Each item is its own request, a few at a time. There is no bulk route on
 * purpose: a failure part-way leaves a report of which items did not change
 * rather than an all-or-nothing that nobody can inspect.
 */

export type BulkAction =
  | { kind: "location"; locationId: string | null }
  | { kind: "category"; categoryId: string | null }
  | { kind: "status"; status: ItemStatus }
  | { kind: "tag"; name: string }
  | { kind: "delete" };

export interface BulkFailure {
  id: string;
  message: string;
}

const CONCURRENCY = 4;

const reason = (problem: unknown): string =>
  problem instanceof ApiError || problem instanceof Error ? problem.message : "Something went wrong.";

async function apply(id: string, action: BulkAction): Promise<void> {
  switch (action.kind) {
    case "location":
      await api.items.update(id, { locationId: action.locationId });
      return;
    case "category":
      await api.items.update(id, { categoryId: action.categoryId });
      return;
    case "status":
      await api.items.update(id, { status: action.status });
      return;
    case "tag": {
      // Tags are replaced as a list, so adding one means reading what is there.
      const { item } = await api.items.get(id);
      if (item.tags.some((tag) => tag.toLowerCase() === action.name.toLowerCase())) return;
      await api.items.update(id, { tags: [...item.tags, action.name] });
      return;
    }
    case "delete":
      await api.items.remove(id);
      return;
  }
}

/** Applies the action to every id and returns the ones that failed. `onProgress` gets the count finished. */
export async function runBulk(
  ids: readonly string[],
  action: BulkAction,
  onProgress: (finished: number) => void,
): Promise<BulkFailure[]> {
  const queue = [...ids];
  const failures: BulkFailure[] = [];
  let finished = 0;
  const worker = async () => {
    for (let id = queue.shift(); id !== undefined; id = queue.shift()) {
      try {
        await apply(id, action);
      } catch (problem) {
        failures.push({ id, message: reason(problem) });
      }
      onProgress((finished += 1));
    }
  };
  await Promise.all(Array.from({ length: Math.min(CONCURRENCY, ids.length) }, worker));
  return failures;
}
